import * as THREE from 'three';
import Application from '../Application';
import { contactShadow, mergedMesh, paintFlat, paintVerticalGradient, srgb, tintedBox, } from './Procedural';
import { configureSceneLights } from './SceneLighting';
import { SCREEN_POSITION } from './ScreenConfig';
import { BRUNO_DESK } from './DecorMode';
/**
 * Refonte du poste de travail : bureau, mur, tapis, tasse et documents.
 *
 * Meme methode que GamerSetup — masquer le noeud baked et poser de la
 * geometrie a sa place — appliquee aux meubles et aux objets poses dessus.
 *
 * Ce que ce remplacement coute, et comment il est compense : la texture bakee
 * du plateau portait TOUTES les ombres des objets poses dessus. Des que le
 * bureau devient de la geometrie, ces ombres partent avec lui et tout se met
 * a flotter. Elles sont donc refaites en taches de contact (voir
 * Procedural.contactShadow), posees sous chaque objet, qu'il soit baked ou
 * procedural.
 *
 * Le mur : le decor n'en a pas. Le fond gris qu'on voyait derriere le bureau
 * etait la paroi du grand cube `Background`, a 36 000 unites — d'ou son aspect
 * plat et lointain. Un vrai mur est pose juste derriere le plateau, ce qui
 * resserre la piece et, surtout, donne aux halos coloriees une surface a
 * lecher : sans lui, les lumieres du dossier 1 n'auraient rien eclaire.
 *
 * Cette classe est construite EN DERNIER dans World : les taches de contact
 * ont besoin que RoomDecor et RoomLights aient fini de poser leurs objets.
 */
/** Charte ForAsTech (kit logo), comme l'intro, l'encart et les LED. */
const BRAND_BLUE = 0x2f6bff;
const BRAND_TEAL = 0x12c2b0;
const BRAND_ORANGE = 0xff7a1a;
/** Objets baked qui restent, et sous lesquels il faut remettre une ombre. */
const BAKED_PROPS = [
    'binder_1',
    'binder_2',
    'rubiks',
    'figurines',
    'livres',
];
/** Epaisseur du tapis : les objets poses dessus prennent leur ombre un cran
 * plus haut que ceux poses a meme le plateau. */
const MAT_THICKNESS = 10;
const MAT_W = 2600;
const MAT_D = 1600;
const MAT_X = -120;
const MAT_Z = 820;
export default class DeskSet {
    constructor() {
        this.deskTop = 0;
        this.application = new Application();
        this.scene = this.application.scene;
        const desk = this.scene.getObjectByName('desk');
        if (!desk)
            return;
        // La boite est lue AVANT de masquer quoi que ce soit : plusieurs
        // classes se reperent sur celle du bureau, et rien ne garantit qu'un
        // objet invisible soit encore pris en compte par Box3.
        desk.updateWorldMatrix(true, true);
        const box = new THREE.Box3().setFromObject(desk);
        this.deskTop = box.max.y;
        // Le plateau et le mur restent toujours : ce sont eux le cadre de la
        // scene, et le mur large est voulu tel quel.
        this.buildDesk(box);
        this.buildWall(box);
        // Tapis, mug, casque et documents ont leur equivalent dans le poste
        // importe du dossier 1 ; les deux jeux se superposeraient.
        if (!BRUNO_DESK) {
            this.buildDeskMat(box);
            this.buildMug();
            this.buildHeadset();
            this.buildDocuments();
        }
        this.addBakedPropShadows();
        this.placeLights(box);
    }
    /** Boite monde d'un noeud baked, puis masquage de ce noeud. */
    take(name) {
        const node = this.scene.getObjectByName(name);
        if (!node)
            return null;
        node.updateWorldMatrix(true, true);
        const box = new THREE.Box3().setFromObject(node);
        node.visible = false;
        return box;
    }
    add(parts) {
        const mesh = mergedMesh(parts);
        this.scene.add(mesh);
        return mesh;
    }
    /**
     * Plateau epais a chant biseaute sur quatre pieds metalliques, a
     * l'emplacement exact de l'ancien : tout le reste de la scene — moniteur,
     * tour, decor, lumieres — se positionne par rapport a cette boite.
     */
    buildDesk(box) {
        const node = this.scene.getObjectByName('desk');
        if (node)
            node.visible = false;
        const w = box.max.x - box.min.x;
        const d = box.max.z - box.min.z;
        const cx = (box.min.x + box.max.x) / 2;
        const cz = (box.min.z + box.max.z) / 2;
        const top = box.max.y;
        const floor = box.min.y;
        const SLAB = 86;
        const LIP = 16;
        const parts = [];
        // Deux volumes empiles plutot qu'un seul pave : le corps en retrait
        // sous une fine plaque de surface dessine un chant biseaute, ce qui
        // suffit a sortir le plateau de l'aspect « boite ».
        const surface = tintedBox(w, LIP, d, {
            top: 0x6f655a,
            side: 0x4e473f,
        });
        surface.translate(cx, top - LIP / 2, cz);
        parts.push(surface);
        // Veinage : une nappe de fines bandes a peine differenciees, posee
        // sur la surface. Un plateau d'une seule couleur reste une surface
        // morte, et le relief par face ne peut rien pour une face plane.
        const grain = new THREE.PlaneGeometry(w - 6, d - 6, 1, 64);
        grain.rotateX(-Math.PI / 2);
        {
            const pos = grain.attributes.position;
            const colors = new Float32Array(pos.count * 3);
            const pale = srgb(0x7a6f62);
            const dark = srgb(0x655c51);
            const c = new THREE.Color();
            for (let i = 0; i < pos.count; i++) {
                const z = pos.getZ(i);
                // Deux sinusoides de periodes incommensurables : une seule
                // donnerait des rayures manifestement regulieres.
                const t = 0.5 +
                    0.5 * Math.sin(z * 0.0121) * Math.sin(z * 0.0037 + 1.1);
                c.copy(dark).lerp(pale, t);
                colors[i * 3] = c.r;
                colors[i * 3 + 1] = c.g;
                colors[i * 3 + 2] = c.b;
            }
            grain.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        }
        grain.translate(cx, top + 0.6, cz);
        parts.push(grain);
        const core = tintedBox(w - 22, SLAB - LIP, d - 22, {
            top: 0x413b34,
            side: 0x373129,
            bottom: 0x2a2520,
        });
        core.translate(cx, top - LIP - (SLAB - LIP) / 2, cz);
        parts.push(core);
        // Pieds : section carree, en retrait des angles.
        const LEG = 92;
        const INSET = 190;
        const legH = top - SLAB - floor;
        const legs = [
            [box.min.x + INSET, box.min.z + INSET],
            [box.max.x - INSET, box.min.z + INSET],
            [box.min.x + INSET, box.max.z - INSET],
            [box.max.x - INSET, box.max.z - INSET],
        ];
        legs.forEach(([lx, lz]) => {
            const leg = tintedBox(LEG, legH, LEG, {
                top: 0x33373d,
                side: 0x24272c,
                bottom: 0x191b1e,
            });
            leg.translate(lx, floor + legH / 2, lz);
            parts.push(leg);
        });
        // Traverse arriere : sans elle les quatre pieds flottent visuellement
        // les uns par rapport aux autres.
        const rail = tintedBox(w - 2 * INSET - LEG, 70, 46, {
            top: 0x2c3035,
            side: 0x202326,
        });
        rail.translate(cx, floor + legH * 0.22, box.min.z + INSET);
        parts.push(rail);
        this.add(parts);
    }
    /**
     * Cloison pleine : degrade vertical sur la face interieure, teinte mate
     * et plus sombre sur les cinq autres.
     *
     * Un mur doit etre un VOLUME, pas un plan. Un plan n'a qu'une face :
     * en vue libre, des qu'on passe derriere, il est elimine par le rendu et
     * on voit la piece au travers. Et ses bords gauche et droit flottent dans
     * le vide, alors qu'un vrai mur se termine dans un angle.
     */
    wallBox(w, h, d, inside, low, high, outer) {
        const geo = new THREE.BoxGeometry(w, h, d);
        const pos = geo.attributes.position;
        const colors = new Float32Array(pos.count * 3);
        const lowColor = srgb(low);
        const highColor = srgb(high);
        const outerColor = srgb(outer);
        const c = new THREE.Color();
        // BoxGeometry range ses 24 sommets par face, 4 par face, dans l'ordre
        // +X, -X, +Y, -Y, +Z, -Z.
        const first = { px: 0, nx: 4, pz: 16 }[inside];
        for (let i = 0; i < pos.count; i++) {
            if (i >= first && i < first + 4) {
                // Quatre sommets suffisent : le degrade est lineaire, et le
                // rendu interpole entre le haut et le bas de la face.
                c.copy(lowColor).lerp(highColor, (pos.getY(i) + h / 2) / h);
            }
            else {
                c.copy(outerColor);
            }
            colors[i * 3] = c.r;
            colors[i * 3 + 1] = c.g;
            colors[i * 3 + 2] = c.b;
        }
        geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        return geo;
    }
    /**
     * La piece : mur du fond contre le bureau, et deux murs de retour qui le
     * ferment en angle.
     *
     * Les cotes sont calees sur la camera, pas sur le bureau. En veille elle
     * balaie de x -10 000 a +10 000 a z 10 000, et la vue libre orbite a
     * partir de 4 000 : les retours sont donc poses au-dela, sinon ils
     * passeraient devant le bureau au lieu de l'encadrer. Le fond, lui, reste
     * colle au plateau — c'est lui qui resserre la piece.
     */
    buildWall(box) {
        const GAP = 140;
        const backZ = box.min.z - GAP;
        const floor = box.min.y;
        const HALF_W = 14000;
        const FRONT_Z = 14000;
        // Assez haut pour que l'arete superieure sorte du cadre : en vue
        // libre un peu surelevee, on voyait par-dessus les murs. Pas de
        // plafond pour autant — la camera peut reculer jusqu'a 29 000, et une
        // piece fermee ne montrerait plus qu'une boite grise.
        const HEIGHT = 20000;
        const THICK = 220;
        const LOW = 0x9fa3ab;
        const HIGH = 0xcfd2d8;
        const OUTER = 0x7c8088;
        const parts = [];
        // Fond : assez large pour aller jusqu'au dos des retours, ce qui ferme
        // les angles sans laisser d'interstice.
        const back = this.wallBox((HALF_W + THICK) * 2, HEIGHT, THICK, 'pz', LOW, HIGH, OUTER);
        back.translate(0, floor + HEIGHT / 2, backZ - THICK / 2);
        parts.push(back);
        const sideD = FRONT_Z - backZ;
        const sideZ = (backZ + FRONT_Z) / 2;
        const left = this.wallBox(THICK, HEIGHT, sideD, 'px', LOW, HIGH, OUTER);
        left.translate(-HALF_W - THICK / 2, floor + HEIGHT / 2, sideZ);
        parts.push(left);
        const right = this.wallBox(THICK, HEIGHT, sideD, 'nx', LOW, HIGH, OUTER);
        right.translate(HALF_W + THICK / 2, floor + HEIGHT / 2, sideZ);
        parts.push(right);
        // Plinthes : la jonction mur/sol sans rien est une arete nette qui
        // trahit le decor.
        const skirtTint = { top: 0xb6b9c0, side: 0xd6d9df };
        const backSkirt = tintedBox(HALF_W * 2, 110, 26, skirtTint);
        backSkirt.translate(0, floor + 55, backZ + 13);
        parts.push(backSkirt);
        [-1, 1].forEach((sx) => {
            const skirt = tintedBox(26, 110, sideD, skirtTint);
            skirt.translate(sx * (HALF_W - 13), floor + 55, sideZ);
            parts.push(skirt);
        });
        this.add(parts);
    }
    /**
     * Tapis de bureau. L'ancienne texture en peignait un (le grand rectangle
     * sombre sous le moniteur) ; il redevient un objet, ce qui lui rend une
     * epaisseur et un chant.
     */
    buildDeskMat(box) {
        const top = box.max.y;
        // Assez grand pour porter clavier, souris et pied d'ecran, mais pas
        // au point de recouvrir le plateau : c'est le bois qui doit rester le
        // materiau dominant.
        const mat = tintedBox(MAT_W, MAT_THICKNESS, MAT_D, {
            top: 0x24272c,
            side: 0x16181c,
        });
        mat.translate(MAT_X, top + MAT_THICKNESS / 2, MAT_Z);
        this.add([mat]);
    }
    /**
     * Tasse tournee : corps en ceramique, anse, surface de cafe. L'ancienne
     * etait un volume baked sans relief propre.
     */
    buildMug() {
        const box = this.take('coffee');
        if (!box)
            return;
        const cx = (box.min.x + box.max.x) / 2;
        const cz = (box.min.z + box.max.z) / 2;
        const baseY = box.min.y;
        const R = 150;
        const WALL = 15;
        const H = 330;
        // Profil tourne, de l'axe vers l'exterieur, puis redescendu a
        // l'interieur : c'est ce retour qui donne l'epaisseur de la paroi
        // visible au bord, sans quoi la tasse parait decoupee dans du papier.
        const profile = [
            new THREE.Vector2(0, 0),
            new THREE.Vector2(R * 0.78, 0),
            new THREE.Vector2(R * 0.84, 14),
            new THREE.Vector2(R * 0.95, H * 0.3),
            new THREE.Vector2(R, H),
            new THREE.Vector2(R - WALL, H),
            new THREE.Vector2(R - WALL, H * 0.14),
            new THREE.Vector2(R - WALL - 26, H * 0.09),
            new THREE.Vector2(0, H * 0.09),
        ];
        const body = new THREE.LatheGeometry(profile, 36);
        paintVerticalGradient(body, 0xa8acb4, 0xf3f5f7, 0.7);
        // Anse : un tore complet, a demi noye dans la paroi. Un arc partiel
        // demanderait de caler l'ouverture au degre pres pour ne pas laisser
        // voir ses extremites creuses.
        const handle = new THREE.TorusGeometry(88, 17, 10, 28);
        handle.translate(R - 26, H * 0.52, 0);
        paintVerticalGradient(handle, 0x9aa0a8, 0xe8ebee, 0.7);
        const coffee = new THREE.CircleGeometry(R - WALL - 4, 32);
        coffee.rotateX(-Math.PI / 2);
        coffee.translate(0, H * 0.76, 0);
        paintFlat(coffee, 0x3b2418);
        const mesh = mergedMesh([body, handle, coffee]);
        mesh.position.set(cx, baseY, cz);
        // L'anse est construite sur +X, ou le fauteuil la masque dans la vue
        // d'ensemble ; un quart de tour la ramene vers la camera.
        mesh.rotation.y = -0.7;
        this.scene.add(mesh);
        this.shadowUnder(mesh, 1.5, 0.4);
    }
    /**
     * Casque de jeu sur son support : arceau, coussinets, micro-perche et
     * anneaux RGB sur les coques.
     *
     * Il est redresse sur un pied, alors que le casque baked etait pose a
     * plat. Couche, un casque n'est qu'une paire de disques bruns ; debout il
     * se lit d'un coup d'oeil, et c'est la seule position ou les anneaux
     * lumineux font face a la camera.
     */
    buildHeadset() {
        const box = this.take('casque');
        if (!box)
            return;
        const cx = (box.min.x + box.max.x) / 2;
        const cz = (box.min.z + box.max.z) / 2;
        const baseY = box.min.y;
        const CUP_R = 118;
        const CUP_T = 74;
        const BAND_R = 186;
        const BAND_TUBE = 26;
        const HOOK_Y = 470;
        // Les ecouteurs pendent aux extremites de l'arceau, donc son centre
        // descend d'un rayon sous le crochet.
        const bandY = HOOK_Y - BAND_R;
        const parts = [];
        // --- Support
        const foot = new THREE.CylinderGeometry(1, 1, 1, 32);
        foot.scale(150, 24, 112);
        foot.translate(0, 12, 0);
        paintVerticalGradient(foot, 0x1a1d21, 0x3f454d, 0.6);
        parts.push(foot);
        const post = new THREE.CylinderGeometry(19, 23, HOOK_Y - 20, 16);
        post.translate(0, 20 + (HOOK_Y - 20) / 2, 0);
        paintVerticalGradient(post, 0x24282e, 0x474e57, 0.5);
        parts.push(post);
        const hook = tintedBox(150, 26, 54, {
            top: 0x4a515a,
            side: 0x282c32,
            front: 0x343a41,
        });
        hook.translate(0, HOOK_Y + 2, 0);
        parts.push(hook);
        // --- Arceau : demi-tore, coussinet sous la partie haute
        const band = new THREE.TorusGeometry(BAND_R, BAND_TUBE, 10, 40, Math.PI);
        paintVerticalGradient(band, 0x272b31, 0x565d67, 0.5);
        band.translate(0, bandY, 0);
        parts.push(band);
        const padding = new THREE.TorusGeometry(BAND_R - BAND_TUBE * 0.7, BAND_TUBE * 0.85, 8, 28, Math.PI * 0.62);
        padding.rotateZ(Math.PI * 0.19);
        padding.translate(0, bandY, 0);
        paintFlat(padding, 0x14161a);
        parts.push(padding);
        // --- Coques
        [-1, 1].forEach((sx) => {
            const x = sx * BAND_R;
            const cup = new THREE.CylinderGeometry(CUP_R, CUP_R * 0.9, CUP_T, 30);
            cup.rotateZ(Math.PI / 2);
            cup.translate(x + sx * CUP_T * 0.3, bandY, 0);
            paintVerticalGradient(cup, 0x1d2025, 0x4c535c, 0.65);
            parts.push(cup);
            // Coussinet d'oreille, tourne vers l'interieur.
            const pad = new THREE.CylinderGeometry(CUP_R * 0.94, CUP_R * 0.82, CUP_T * 0.5, 30);
            pad.rotateZ(Math.PI / 2);
            pad.translate(x - sx * CUP_T * 0.3, bandY, 0);
            paintFlat(pad, 0x121417);
            parts.push(pad);
            // Anneau RGB : la teinte tourne avec l'angle autour du tore, ce
            // qui donne l'arc-en-ciel continu des casques de jeu. Une couleur
            // unie n'aurait pas lu comme « RGB ».
            const ring = new THREE.TorusGeometry(CUP_R * 0.68, CUP_R * 0.1, 8, 40);
            {
                const pos = ring.attributes.position;
                const colors = new Float32Array(pos.count * 3);
                const c = new THREE.Color();
                for (let i = 0; i < pos.count; i++) {
                    const angle = Math.atan2(pos.getY(i), pos.getX(i));
                    c.setHSL((angle / (Math.PI * 2) + 1) % 1, 0.88, 0.58).convertSRGBToLinear();
                    colors[i * 3] = c.r;
                    colors[i * 3 + 1] = c.g;
                    colors[i * 3 + 2] = c.b;
                }
                ring.setAttribute('color', new THREE.BufferAttribute(colors, 3));
            }
            ring.rotateY(Math.PI / 2);
            ring.translate(x + sx * (CUP_T * 0.3 + CUP_T / 2 + 3), bandY, 0);
            parts.push(ring);
        });
        // --- Micro-perche, sur la coque gauche
        const boom = new THREE.TorusGeometry(150, 13, 8, 24, Math.PI * 0.55);
        boom.rotateY(Math.PI / 2);
        boom.rotateX(Math.PI * 0.1);
        boom.translate(-BAND_R - CUP_T * 0.3, bandY - 20, 0);
        paintFlat(boom, 0x202429);
        parts.push(boom);
        const capsule = new THREE.SphereGeometry(26, 16, 12);
        capsule.scale(1, 1, 1.5);
        capsule.translate(-BAND_R - CUP_T * 0.3, bandY - 150, 112);
        paintFlat(capsule, 0x2b3037);
        parts.push(capsule);
        const mesh = mergedMesh(parts);
        mesh.position.set(cx, baseY, cz);
        // Legerement de trois quarts : de face, l'arceau se reduit a un trait.
        mesh.rotation.y = -0.35;
        this.scene.add(mesh);
        this.shadowForBox(new THREE.Box3(new THREE.Vector3(cx - 190, baseY, cz - 150), new THREE.Vector3(cx + 190, baseY, cz + 150)), 1.3, 0.42);
    }
    /** Une pile de feuilles, legerement desalignees comme une vraie pile. */
    paperStack(box, parts, sheetH = 8) {
        const w = (box.max.x - box.min.x) * 0.94;
        const d = (box.max.z - box.min.z) * 0.94;
        const cx = (box.min.x + box.max.x) / 2;
        const cz = (box.min.z + box.max.z) / 2;
        const h = box.max.y - box.min.y;
        const count = Math.max(6, Math.round(h / sheetH));
        for (let i = 0; i < count; i++) {
            const sheet = tintedBox(w, sheetH * 0.82, d, {
                top: 0xf2f1ec,
                // Le chant d'une pile est plus sombre que la face : c'est lui
                // qui fait compter les feuilles.
                side: 0xcecabf,
                bottom: 0xb8b4aa,
            });
            // Desalignement deterministe : un hasard reel changerait la pile a
            // chaque chargement, et une pile parfaitement alignee est un pave.
            sheet.rotateY(Math.sin(i * 2.7) * 0.012);
            sheet.translate(cx + Math.sin(i * 1.9) * w * 0.012, box.min.y + (i + 0.5) * sheetH, cz + Math.cos(i * 2.3) * d * 0.012);
            parts.push(sheet);
        }
    }
    /**
     * Documents : les deux piles, la bannette a deux niveaux et la feuille
     * imprimee posee sur le plateau.
     */
    buildDocuments() {
        const parts = [];
        // Les boites sont retenues pour poser une ombre par objet : une seule
        // tache a l'echelle du maillage fusionne couvrirait tout le cote
        // gauche du bureau d'un gros flou.
        const shadowBoxes = [];
        const stack1 = this.take('paper_stack_1');
        if (stack1) {
            this.paperStack(stack1, parts);
            shadowBoxes.push(stack1.clone());
        }
        const stack2 = this.take('paper_stack_2');
        if (stack2)
            this.paperStack(stack2, parts, 7);
        // Bannette : deux plateaux sur quatre montants.
        const trayLow = this.take('paper_holder_bottom');
        const trayHigh = this.take('paper_holder_top');
        const tray = trayLow || trayHigh;
        if (tray) {
            const union = trayLow && trayHigh ? trayLow.union(trayHigh) : tray;
            const w = union.max.x - union.min.x;
            const d = union.max.z - union.min.z;
            const cx = (union.min.x + union.max.x) / 2;
            const cz = (union.min.z + union.max.z) / 2;
            const base = union.min.y;
            const total = union.max.y - base;
            shadowBoxes.push(union.clone());
            [0, total * 0.62].forEach((lift) => {
                const shelf = tintedBox(w, 22, d, {
                    top: 0x3d434c,
                    side: 0x272b31,
                });
                shelf.translate(cx, base + lift + 11, cz);
                parts.push(shelf);
                const rim = tintedBox(w, 46, 18, {
                    top: 0x454c56,
                    side: 0x2b3037,
                });
                rim.translate(cx, base + lift + 34, cz + d / 2 - 9);
                parts.push(rim);
            });
            [
                [-1, -1],
                [1, -1],
                [-1, 1],
                [1, 1],
            ].forEach(([sx, sz]) => {
                const post = tintedBox(26, total * 0.62, 26, {
                    top: 0x3d434c,
                    side: 0x23272c,
                });
                post.translate(cx + sx * (w / 2 - 22), base + total * 0.31, cz + sz * (d / 2 - 22));
                parts.push(post);
            });
        }
        // Feuille imprimee : la boite d'origine est un plan d'epaisseur nulle,
        // on lui en donne une.
        const sheetBox = this.take('paper');
        if (sheetBox) {
            const w = sheetBox.max.x - sheetBox.min.x;
            const d = sheetBox.max.z - sheetBox.min.z;
            const cx = (sheetBox.min.x + sheetBox.max.x) / 2;
            const cz = (sheetBox.min.z + sheetBox.max.z) / 2;
            const y = sheetBox.min.y;
            const sheet = tintedBox(w, 7, d, {
                top: 0xf6f5f1,
                side: 0xd9d5cb,
            });
            sheet.translate(cx, y + 3.5, cz);
            parts.push(sheet);
            shadowBoxes.push(sheetBox.clone());
            // Lignes de texte : des barres fines et claires, de longueurs
            // inegales. C'est ce qui fait lire « document » plutot que
            // « rectangle blanc ».
            const lines = 11;
            for (let i = 0; i < lines; i++) {
                const len = w * (0.42 + 0.3 * Math.abs(Math.sin(i * 1.7)));
                const line = tintedBox(len, 2, d * 0.028, {
                    top: 0x8d8a84,
                    side: 0x8d8a84,
                });
                line.translate(cx - w * 0.22 + len / 2 - w * 0.08, y + 8, cz - d * 0.3 + i * d * 0.058);
                parts.push(line);
            }
        }
        if (parts.length)
            this.add(parts);
        shadowBoxes.forEach((b) => this.shadowForBox(b, 1.18, 0.34));
    }
    /**
     * Tache de contact, dimensionnee sur l'emprise au sol d'une boite.
     *
     * La hauteur de pose est deduite du plateau, pas du bas de l'objet :
     * plusieurs accessoires bakes descendent sous la surface (les classeurs
     * commencent a -567 pour un plateau a -452), et une ombre calee sur leur
     * minimum se retrouve noyee dans le meuble, donc invisible. C'est ce qui
     * faisait que rien ne se posait.
     */
    shadowForBox(box, spread = 1.4, opacity = 0.42) {
        const w = (box.max.x - box.min.x) * spread;
        const d = (box.max.z - box.min.z) * spread;
        if (!Number.isFinite(w) || !Number.isFinite(d) || w <= 0)
            return;
        const cx = (box.min.x + box.max.x) / 2;
        const cz = (box.min.z + box.max.z) / 2;
        // Sur le tapis, l'ombre monte de son epaisseur, sinon elle disparait
        // dessous.
        const onMat = Math.abs(cx - MAT_X) < MAT_W / 2 && Math.abs(cz - MAT_Z) < MAT_D / 2;
        const y = this.deskTop + (onMat ? MAT_THICKNESS : 0) + 2;
        const shadow = contactShadow(w, d, opacity);
        shadow.position.set(cx, y, cz);
        this.scene.add(shadow);
    }
    shadowUnder(object, spread = 1.4, opacity = 0.42) {
        object.updateWorldMatrix(true, true);
        this.shadowForBox(new THREE.Box3().setFromObject(object), spread, opacity);
    }
    shadowForNode(name, spread = 1.3, opacity = 0.38) {
        const node = this.scene.getObjectByName(name);
        if (!node)
            return;
        node.updateWorldMatrix(true, true);
        this.shadowForBox(new THREE.Box3().setFromObject(node), spread, opacity);
    }
    addBakedPropShadows() {
        BAKED_PROPS.forEach((name) => {
            const node = this.scene.getObjectByName(name);
            if (node && node.visible)
                this.shadowUnder(node, 1.3, 0.38);
        });
        // Clavier, souris et pied d'ecran sont desormais proceduraux, mais les
        // noeuds bakes masques restent la et donnent leur emprise exacte.
        this.shadowForNode('keyboard', 1.12, 0.42);
        this.shadowForNode('mouse', 1.5, 0.4);
        this.shadowForBox(new THREE.Box3(new THREE.Vector3(-300, this.deskTop, SCREEN_POSITION.z - 206), new THREE.Vector3(300, this.deskTop, SCREEN_POSITION.z + 154)), 1.45, 0.42);
    }
    /**
     * Les trois halos. Positions derivees de la boite du bureau, pour qu'ils
     * suivent si le meuble bouge.
     */
    placeLights(box) {
        const top = box.max.y;
        configureSceneLights([
            // Ecran : la nappe bleue qui lave le mur derriere le moniteur.
            // C'est elle qui porte l'effet, comme la lumiere « tv » du 1.
            //
            // Elle est placee pres du mur, et non pres de l'ecran : depuis
            // l'ecran, classeurs et tapis sont a la meme distance que le mur
            // et viraient au bleu avec lui. Reculee, elle concentre sa nappe
            // sur la paroi et laisse le plateau tranquille.
            {
                position: new THREE.Vector3(0, SCREEN_POSITION.y + 150, box.min.z - 40),
                color: BRAND_BLUE,
                radius: 2900,
                strength: 0.5,
            },
            // Tour de watercooling : elle est a DROITE du bureau, pas a
            // gauche. Le halo teal pose du mauvais cote verdissait le plateau
            // et la bannette a documents.
            {
                position: new THREE.Vector3(box.max.x + 500, box.min.y + 1300, box.max.z - 700),
                color: BRAND_TEAL,
                radius: 3000,
                strength: 0.22,
            },
            // Lampe chaude au-dessus des documents, pour casser le tout-froid.
            {
                position: new THREE.Vector3(box.min.x + 900, top + 850, box.min.z + 700),
                color: BRAND_ORANGE,
                radius: 2600,
                strength: 0.3,
            },
        ]);
    }
}
